import {USER_KEYS} from "@/api/userApi";
import Button from "@/components/Common/Button";
import TextButton from "@/components/Common/TextButton";
import Loader from "@/components/Common/kpi/Loader";
import GoogleLoginButton from "@/components/registration/GoogleLoginButton";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useLogin} from "@/hooks/useLogin";
import {useIsLoggedIn} from "@/hooks/useUserHooks";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {isFinishRegistrationModalVisibleAtom} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import dispatchGoogleAnalyticsEvent from "@/utils/dispatchGoogleAnalyticsEvent";
import {identifyHubSpotVisitor} from "@/utils/hubspot";
import {useMutation} from "@tanstack/react-query";
import {useNavigate, useSearch} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {useShallow} from "zustand/shallow";

const RegistrationBottomView: FC<{isInsideModal?: boolean}> = ({isInsideModal = false}) => {
  const {t} = useTranslation("common");
  const navigate = useNavigate();
  const {login} = useLogin();
  const {refetch: refetchUser} = useFetchUser();
  const setIsFinishRegistrationModalVisible = useSetAtom(isFinishRegistrationModalVisibleAtom);
  const search = useSearch({strict: false});
  const joinPageReferrer = search.join_ref;
  const isLoggedIn = useIsLoggedIn();
  const isRegisteringAfterDirectPurchase = useRegistrationStore(state => state.isRegisteringAfterDirectPurchase);
  const registeredUserId = useRegistrationStore(state => state.userId);
  const registeredUserSubscription = useRegistrationStore(state => state.package_subscription);
  const step = useRegistrationStore(state => state.step);
  const errors = useRegistrationStore(useShallow(state => Object.values(state.errors).filter(error => !!error)));
  const interests = useRegistrationStore(state => state.interests);
  const canFinishRegistration = !!interests?.length && !errors.length;
  const validateInterestSelection = useRegistrationStore(state => state.validateInterestSelection);
  const goToNextPage = useRegistrationStore(state => state.goToNextPage);
  const registerCompany = useRegistrationStore(state => state.register);

  const {mutate: register, isPending} = useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserList,
    mutationFn: () => registerCompany(),
    onSuccess: async response => {
      identifyHubSpotVisitor(useRegistrationStore.getState().email);

      if (isRegisteringAfterDirectPurchase) {
        capturePostHogEvent(PostHogEvents.CompanyRegistrationFinishedPaymentLink, {
          packageCategory: registeredUserSubscription?.package_category,
          packageName: registeredUserSubscription?.name,
          packageType: registeredUserSubscription?.package_type,
          registeredUserId,
        });
        dispatchGoogleAnalyticsEvent(
          "registration_events",
          "registration_finished",
          `Successfully registered user #${registeredUserId}`,
          {
            registered_user_id: registeredUserId,
            package_name: registeredUserSubscription?.name,
            package_type: registeredUserSubscription?.package_type,
            package_category: registeredUserSubscription?.package_category,
            registered_via_payment_link: true,
          },
        );
      } else {
        const registeredUser = response?.data?.user;

        if (registeredUser) {
          capturePostHogEvent(PostHogEvents.CompanyRegistrationFinished, {
            hubspotId: registeredUser.hubspot_id,
            registeredUserId: registeredUser.id,
            ...(joinPageReferrer && {joinPageReferrer}),
          });
          dispatchGoogleAnalyticsEvent(
            "registration_events",
            "registration_finished",
            `Successfully registered user #${registeredUser.id}`,
            {
              registered_user_id: registeredUser.id,
              hubspot_id: registeredUser.hubspot_id,
              registered_via_payment_link: false,
            },
          );
        }
      }

      if (isRegisteringAfterDirectPurchase) {
        await refetchUser();
        setIsFinishRegistrationModalVisible(false);
      }

      navigate({to: isRegisteringAfterDirectPurchase ? "/welcome" : "/image-upload", replace: true});

      if (!isLoggedIn)
        login({
          email: useRegistrationStore.getState().email,
          password: useRegistrationStore.getState().password,
        });
    },
  });
  const isSignUpDisabled = !canFinishRegistration || isPending;

  const proceedWithRegistrationSteps = () => {
    if (step === "interest_selection") {
      validateInterestSelection() && register();
    } else {
      goToNextPage();
    }
  };

  return (
    <div className={"flex flex-col items-center gap-5 " + (step !== "credentials" ? "pb-10" : "")}>
      {step === "interest_selection" ? (
        <>
          <div className="flex flex-col gap-3">
            <Button
              customButtonId="registration-create-account-button"
              dataTestId="registration-create-account-button"
              onClick={proceedWithRegistrationSteps}
              isDisabled={isSignUpDisabled}
              className="h-15 w-full sm:w-105"
              buttonStyle="Gradient">
              {isPending ? <Loader /> : t("createAccount")}
            </Button>
            {!isLoggedIn && (
              <>
                <div className="flex w-full max-w-105 items-center gap-5">
                  <div className="bg-primary-light-solid h-px flex-1" />
                  <span className="text-neutral-grey-dark text-xs">{t("or")}</span>
                  <div className="bg-primary-light-solid h-px flex-1" />
                </div>
                <GoogleLoginButton
                  payload={{
                    address_city: useRegistrationStore.getState().addressCity!,
                    address_postal: useRegistrationStore.getState().addressPostal!,
                    address_street: useRegistrationStore.getState().addressStreet!,
                    company_name: useRegistrationStore.getState().companyName,
                    country: useRegistrationStore.getState().country!,
                    interests: useRegistrationStore.getState().interests?.join(","),
                    language_code: useRegistrationStore.getState().languageCode,
                    learn_about_us: useRegistrationStore.getState().selectedLearnAboutUs ?? "",
                    learn_about_us_name: useRegistrationStore.getState().learnAboutUsExtra ?? "",
                    phonenumber: useRegistrationStore.getState().phoneNumber,
                    place_description: useRegistrationStore.getState().placeDescription!,
                    place_id: useRegistrationStore.getState().placeId!,
                    tiktok_username: useRegistrationStore
                      .getState()
                      .companyTikTokUsername?.replace("@", "")
                      .toLowerCase(),
                    username: useRegistrationStore.getState().companyInstagramUsername?.replace("@", "").toLowerCase(),
                    website: useRegistrationStore.getState().companyWebsite,
                  }}
                  text={t("sign_up_with_google")}
                  isDisabled={isSignUpDisabled}
                  className="h-15 w-full sm:w-105"
                />
              </>
            )}
          </div>
          {!!errors.length && (
            <div className="bg-pretty-pink-200 dark:bg-danger-light-red text-neutral-black dark:text-danger-crimson dark:border-danger-medium-red my-2.5 w-full rounded-md p-2.5 dark:border">
              <ul className="mb-0">
                {errors.map((error, idx) => (
                  <li key={idx}>{t(error as "First name is required")}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <Button
          customButtonId="registration-next-button"
          dataTestId="registration-next-button"
          className={`h-14 w-48 ${isInsideModal ? "sm:min-w-90" : ""}`}
          buttonStyle="Blue"
          onClick={proceedWithRegistrationSteps}>
          {t("next")}
        </Button>
      )}
      {step === "interest_selection" && (
        <span className="text-neutral-grey-dark text-center text-sm font-medium">
          {t("createAccountDisclaimer")}
          <TextButton
            onClick={() => {
              window.open("/terms-conditions/", "_blank");
            }}
            text={"Refluenced " + t("ToS")}></TextButton>{" "}
          {t("and")}{" "}
          <TextButton
            className="text-primary-dark-solid hover:text-primary-medium-solid"
            onClick={() => {
              window.open("/privacy-policy/", "_blank");
            }}
            text={t("privacy_policy")}
          />
          {t("allowContact")}
        </span>
      )}
    </div>
  );
};

export default RegistrationBottomView;
